import { useState, useEffect, useRef, useCallback } from 'react';
import { BACKEND_URL } from '@/config/api';

const WS_BASE = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

export const useChannelWebSocket = (channelId, token, user, enabled = true) => {
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const wsRef = useRef(null);
  const handlersRef = useRef({});
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  const connect = useCallback(() => {
    if (!channelId || !enabled || !token) return;

    const wsUrl = `${WS_BASE}/api/ws/channel/${channelId}?token=${token}`;
    console.log('[Channel WS] Connecting to:', wsUrl);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[Channel WS] Connected');
      setIsConnected(true);

      // Start ping interval
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'channel_state':
            setOnlineUsers(data.online_users || []);
            setTypingUsers(data.typing_users || []);
            break;
          
          case 'user_joined':
            setOnlineUsers(data.online_users || []);
            handlersRef.current.onUserJoined?.(data.user);
            break;
          
          case 'user_left':
            setOnlineUsers(data.online_users || []);
            handlersRef.current.onUserLeft?.(data.user);
            break;
          
          case 'new_message':
            handlersRef.current.onNewMessage?.(data.message);
            break;
          
          case 'message_updated':
            handlersRef.current.onMessageUpdated?.(data.message);
            break;
          
          case 'message_deleted':
            handlersRef.current.onMessageDeleted?.(data.message_id);
            break;
          
          case 'reaction_updated':
            handlersRef.current.onReactionUpdated?.(data.message_id, data.reactions);
            break;
          
          case 'typing_update':
            setTypingUsers(data.typing_users || []);
            break;
          
          case 'pong':
            // Keep-alive response
            break;
          
          default:
            console.log('[Channel WS] Unknown message type:', data.type);
        }
      } catch (e) {
        console.error('[Channel WS] Failed to parse message:', e);
      }
    };

    ws.onclose = () => {
      console.log('[Channel WS] Disconnected');
      setIsConnected(false);
      clearInterval(pingIntervalRef.current);

      // Attempt to reconnect after 3 seconds
      if (enabled && channelId) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('[Channel WS] Attempting to reconnect...');
          connect();
        }, 3000);
      }
    };

    ws.onerror = (error) => {
      console.error('[Channel WS] Error:', error);
    };
  }, [channelId, token, enabled]);

  useEffect(() => {
    connect();

    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      clearInterval(pingIntervalRef.current);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  const setHandlers = useCallback((handlers) => {
    handlersRef.current = handlers;
  }, []);

  const sendNewMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'new_message',
        message
      }));
    }
  }, []);

  const sendMessageUpdated = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message_updated',
        message
      }));
    }
  }, []);

  const sendMessageDeleted = useCallback((messageId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message_deleted',
        message_id: messageId
      }));
    }
  }, []);

  const sendReactionUpdated = useCallback((messageId, reactions) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'reaction_updated',
        message_id: messageId,
        reactions
      }));
    }
  }, []);

  const sendTyping = useCallback((isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        is_typing: isTyping
      }));
    }
  }, []);

  return {
    isConnected,
    onlineUsers,
    typingUsers,
    setHandlers,
    sendNewMessage,
    sendMessageUpdated,
    sendMessageDeleted,
    sendReactionUpdated,
    sendTyping
  };
};
